import React, { forwardRef, NamedExoticComponent } from 'react';
import MuiButton, { LoadingButtonProps as MuiButtonProps } from '@mui/lab/LoadingButton';
import { BpDesignSystem } from '../../types';
import { IconName } from '../Icon/types';
import { Icon } from '../Icon';

export type ButtonProps = BpDesignSystem.PropsWithChildren<
  Omit<MuiButtonProps, 'css' | 'startIcon' | 'endIcon' | 'data-test' | 'color'> & {
    /**
     * color variant
     */
    color?: 'primary' | 'secondary' | 'default';
    /**
     * icon name placed before text
     */
    startIcon?: IconName | Exclude<React.ReactNode, string>;
    /**
     * icon name placed after text
     */
    endIcon?: IconName | Exclude<React.ReactNode, string>;
    /**
     * mark as error
     */
    error?: boolean;
    className?: string;
    /**
     * mark as active
     */
    active?: boolean;

    /**
     * adds progress indicator to the button
     */
    loading?: boolean;
    dataTestType?: string;
    download?: string;
  }
>;

const getButtonType = (color?: string, error?: boolean): string => {
  if (error || color === 'secondary') return 'error';
  return color ?? 'default';
};

export const Button = forwardRef<HTMLButtonElement & { download?: string }, ButtonProps>(
  (
    {
      active,
      error,
      dataTest,
      startIcon,
      endIcon,
      className,
      loading,
      disabled,
      dataTestType,
      children,
      download,
      ...props
    },
    ref
  ) => {
    const isIconButton = !children && (startIcon || endIcon);

    const startIconComponent = startIcon ? (
      isIconName(startIcon) ? (
        <Icon name={startIcon} color="inherit" />
      ) : (
        startIcon
      )
    ) : undefined;

    const endIconComponent = endIcon ? (
      isIconName(endIcon) ? (
        <Icon name={endIcon} color="inherit" />
      ) : (
        endIcon
      )
    ) : undefined;

    const targetChildren = isIconButton ? startIconComponent || endIconComponent : children;

    const anchorExtraProps = download
      ? {
          download
        }
      : {};

    return (
      <MuiButton
        {...props}
        className={className}
        disabled={disabled}
        aria-current={active}
        data-test-type={dataTestType ?? getButtonType(props.color, error)}
        data-test-active={active}
        data-test={dataTest ?? 'button'}
        color={error ? 'secondary' : (props.color ?? 'default')}
        variant={active ? 'contained' : props.variant}
        startIcon={!isIconButton ? startIconComponent : undefined}
        endIcon={!isIconButton ? endIconComponent : undefined}
        children={targetChildren}
        loading={loading}
        disableFocusRipple
        loadingPosition={
          isIconButton ? 'center' : !!endIconComponent ? 'end' : !!startIconComponent ? 'start' : 'center'
        }
        ref={ref}
        {...anchorExtraProps}
      />
    );
  }
);

export const PrimaryButton = {
  Outlined: (props: Partial<Omit<ButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button variant="outlined" color="primary" children={props.children} {...props} />
  ),
  Contained: (props: Partial<Omit<ButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button variant="contained" color="primary" children={props.children} {...props} />
  ),
  Text: (props: Partial<Omit<ButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button color="primary" children={props.children} {...props} />
  )
} as const;
(PrimaryButton.Outlined as NamedExoticComponent).displayName = 'PrimaryButton.Outlined';
(PrimaryButton.Contained as NamedExoticComponent).displayName = 'PrimaryButton.Contained';
(PrimaryButton.Text as NamedExoticComponent).displayName = 'PrimaryButton.Text';

export const DefaultButton = {
  Outlined: (props: Partial<Omit<ButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button variant="outlined" children={props.children} {...props} />
  ),
  Contained: (props: Partial<Omit<ButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button variant="contained" children={props.children} {...props} />
  ),
  Text: (props: Partial<Omit<ButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button children={props.children} {...props} />
  )
} as const;
(DefaultButton.Outlined as NamedExoticComponent).displayName = 'DefaultButton.Outlined';
(DefaultButton.Contained as NamedExoticComponent).displayName = 'DefaultButton.Contained';
(DefaultButton.Text as NamedExoticComponent).displayName = 'DefaultButton.Text';

export const ErrorButton = {
  Outlined: (props: Partial<Omit<ButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button variant="outlined" error children={props.children} {...props} />
  ),
  Contained: (props: Partial<Omit<ButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button variant="contained" error children={props.children} {...props} />
  ),
  Text: (props: Partial<Omit<ButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button error children={props.children} {...props} />
  )
} as const;

(ErrorButton.Outlined as NamedExoticComponent).displayName = 'ErrorButton.Outlined';
(ErrorButton.Contained as NamedExoticComponent).displayName = 'ErrorButton.Contained';
(ErrorButton.Text as NamedExoticComponent).displayName = 'ErrorButton.Text';

function isIconName(icon: IconName | Exclude<React.ReactNode, string>): icon is IconName {
  return typeof icon === 'string';
}
